import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  analyzeOffGridSizing,
  fetchOffGridScope,
  hasLoadData,
} from "../services/offGridCalculatorApi";
import { formatReportDateShort } from "../utils/energyReportData";
import { parseApiError } from "../utils/parseApiError";
import { ApiErrorAlert } from "../components/ErrorComponents";

const ALL_BRANCHES = "__all__";
const MODE_ALL_MAINS = "all_mains";
const MODE_CUSTOM = "custom";
const MAIN_SLOTS = [1, 2];
const SIZING_WORST = "worst";
const SIZING_BEST = "best";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const sizingCaseLabel = (sizingCase) =>
  sizingCase === SIZING_BEST ? "Best case" : "Worst case";

const categoryLabel = (category, isMain) => {
  if (isMain || category === "GRID") return "Main";
  if (category === "MACHINE" || category === "LOAD") return "Machine";
  return category || "Load";
};

const ResultTile = ({ label, value, detail }) => (
  <div style={styles.resultTile}>
    <span style={styles.resultLabel}>{label}</span>
    <strong style={styles.resultValue}>{value}</strong>
    {detail && <span style={styles.resultDetail}>{detail}</span>}
  </div>
);

const fmt = (value) => Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });
const fmtKwh = (wh) => `${fmt((Number(wh) || 0) / 1000)} kWh`;
const fmtKwp = (wp) => `${fmt((Number(wp) || 0) / 1000)} kWp`;
const fmtKw = (w) => `${fmt((Number(w) || 0) / 1000)} kW`;

const SolarSystemSection = ({ loadAnalysis, solarSystem, constants, sizingCase }) => {
  if (!solarSystem) return null;

  const { totalDailyEnergyWh, maxNighttimeLoadWh, peakInstantaneousLoadW } = loadAnalysis || {};
  const { solarWp, batteryWh, inverterW, monthUsed } = solarSystem;
  const psh = solarSystem.psh ?? solarSystem.pshUsed ?? constants?.pshUsed ?? 0;
  const caseKey = solarSystem.sizingCase ?? constants?.sizingCase ?? sizingCase ?? SIZING_WORST;
  const monthLabel = monthUsed ? MONTH_NAMES[monthUsed - 1] : "—";
  const {
    systemEfficiency = 0.75,
    batteryDod = 0.9,
    safetyMargin = 1.25,
  } = constants || {};

  return (
    <div style={styles.solarSection}>
      <h3 style={styles.solarSectionTitle}>Recommended Solar System</h3>
      <div style={styles.resultsGrid}>
        <ResultTile
          label="Solar panels"
          value={fmtKwp(solarWp)}
          detail={
            totalDailyEnergyWh > 0
              ? `${fmtKwh(totalDailyEnergyWh)} ÷ (${psh} × ${systemEfficiency}) = ${fmtKwp(solarWp)} · ${sizingCaseLabel(caseKey)}`
              : null
          }
        />
        <ResultTile
          label="Battery"
          value={fmtKwh(batteryWh)}
          detail={
            maxNighttimeLoadWh > 0
              ? `${fmtKwh(maxNighttimeLoadWh)} ÷ ${batteryDod} = ${fmtKwh(batteryWh)}`
              : null
          }
        />
        <ResultTile
          label="Inverter"
          value={fmtKw(inverterW)}
          detail={
            peakInstantaneousLoadW > 0
              ? `${fmtKw(peakInstantaneousLoadW)} × ${safetyMargin} = ${fmtKw(inverterW)}`
              : null
          }
        />
        <ResultTile
          label={`${sizingCaseLabel(caseKey)} PSH`}
          value={`${Number(psh).toFixed(1)} h/day`}
          detail={monthLabel}
        />
      </div>
    </div>
  );
};

const SizingBlock = ({ result, sizingCase }) => {
  const { name, badge, machineNames, isCombined, loadAnalysis, solarSystem, constants, error } =
    result || {};

  if (error || !loadAnalysis || !hasLoadData(loadAnalysis)) {
    return (
      <div style={styles.mainBlock}>
        <div style={styles.mainBlockHead}>
          <span>{name}</span>
          {badge && <span style={styles.badge}>{badge}</span>}
        </div>
        <p style={styles.muted}>{error || "No energy data in the past year."}</p>
      </div>
    );
  }

  const peakDayLabel = loadAnalysis.peakDayDate
    ? formatReportDateShort(loadAnalysis.peakDayDate) || "—"
    : "—";

  const dailyEnergyDetail = isCombined
    ? `Sum of peak-day energy across ${machineNames?.length || 0} machines · past year`
    : `Peak day ${peakDayLabel} · past year`;

  const nighttimeDetail = isCombined
    ? "Combined max 18:00–06:00 if all machines run together · past year"
    : "Highest 18:00–06:00 consumption in the past year";

  const peakLoadDetail = isCombined
    ? "Combined peak draw if all machines run together · past year"
    : "Highest draw in the past year";

  return (
    <div style={styles.mainBlock}>
      <div style={styles.mainBlockHead}>
        <span>{name}</span>
        {badge && <span style={styles.badge}>{badge}</span>}
      </div>

      {machineNames?.length > 0 && (
        <p style={styles.machineListNote}>{machineNames.join(", ")}</p>
      )}

      <div style={styles.analyzerSection}>
        <h3 style={styles.analyzerTitle}>Load Analysis</h3>
        <div style={styles.resultsGrid}>
          <ResultTile
            label="Total daily energy"
            value={fmtKwh(loadAnalysis.totalDailyEnergyWh)}
            detail={dailyEnergyDetail}
          />
          <ResultTile
            label="Max nighttime load"
            value={fmtKwh(loadAnalysis.maxNighttimeLoadWh)}
            detail={nighttimeDetail}
          />
          <ResultTile
            label="Peak instantaneous load"
            value={fmtKw(loadAnalysis.peakInstantaneousLoadW)}
            detail={peakLoadDetail}
          />
        </div>
      </div>

      <SolarSystemSection
        loadAnalysis={loadAnalysis}
        solarSystem={solarSystem}
        constants={constants}
        sizingCase={sizingCase}
      />
    </div>
  );
};

const OffGridCalculatorTab = () => {
  const username = localStorage.getItem("username") || "";

  const [scope, setScope] = useState(null);
  const [scopeLoading, setScopeLoading] = useState(true);
  const [scopeError, setScopeError] = useState(null);

  const [viewMode, setViewMode] = useState(MODE_ALL_MAINS);
  const [branchFilter, setBranchFilter] = useState(ALL_BRANCHES);
  const [mainSlotFilter, setMainSlotFilter] = useState("1");
  const [sizingCase, setSizingCase] = useState(SIZING_WORST);

  const [mainCombinedResult, setMainCombinedResult] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [analyzeError, setAnalyzeError] = useState(null);

  const [selectedNames, setSelectedNames] = useState(new Set());
  const [customResult, setCustomResult] = useState(null);
  const [customLoading, setCustomLoading] = useState(false);

  const loadScope = useCallback(async () => {
    if (!username) {
      setScopeLoading(false);
      setScopeError("Please log in to use the calculator.");
      return;
    }

    setScopeLoading(true);
    setScopeError(null);
    try {
      const scopeMainSlot = viewMode === MODE_CUSTOM ? "all" : mainSlotFilter;
      const data = await fetchOffGridScope(username, branchFilter, scopeMainSlot);
      setScope(data);
    } catch (err) {
      console.error("Scope fetch failed:", err);
      const parsed = err.parsedError || parseApiError(err);
      setScopeError({ ...parsed, retryable: true });
      setScope(null);
    } finally {
      setScopeLoading(false);
    }
  }, [username, branchFilter, mainSlotFilter, viewMode]);

  useEffect(() => {
    loadScope();
  }, [loadScope]);

  useEffect(() => {
    if (viewMode === MODE_ALL_MAINS) {
      setMainSlotFilter("1");
    }
  }, [branchFilter, viewMode]);

  const branchOptions = scope?.branches ?? [];
  const machinesInScope = scope?.machines ?? [];
  const mainsToSize = scope?.mains ?? [];
  const hasAnyMains = scope?.hasAnyMains ?? false;
  const dateRange = scope?.dateRange;

  const loadAllMains = useCallback(async () => {
    if (!username || viewMode !== MODE_ALL_MAINS) return;

    setBulkLoading(true);
    setAnalyzeError(null);
    setMainCombinedResult(null);
    try {
      const data = await analyzeOffGridSizing({
        username,
        mode: MODE_ALL_MAINS,
        branch: branchFilter,
        mainSlot: mainSlotFilter,
        sizingCase,
        combine: true,
      });
      setMainCombinedResult(
        data.combined
          ? { ...data.combined, name: `Main ${mainSlotFilter} — Combined` }
          : null,
      );
    } catch (err) {
      console.error("Bulk sizing failed:", err);
      const parsed = err.parsedError || parseApiError(err);
      setAnalyzeError({ ...parsed, retryable: true });
      setMainCombinedResult(null);
    } finally {
      setBulkLoading(false);
    }
  }, [username, viewMode, branchFilter, mainSlotFilter, sizingCase]);

  useEffect(() => {
    if (scopeLoading || !scope || viewMode !== MODE_ALL_MAINS) return;
    loadAllMains();
  }, [scopeLoading, scope, viewMode, loadAllMains]);

  const loadCustomSelection = useCallback(async () => {
    const names = Array.from(selectedNames);
    if (!username || !names.length) {
      setCustomResult(null);
      return;
    }

    setCustomLoading(true);
    setCustomResult(null);
    setAnalyzeError(null);
    try {
      const data = await analyzeOffGridSizing({
        username,
        mode: MODE_CUSTOM,
        branch: branchFilter,
        machineNames: names,
        sizingCase,
        combine: true,
      });
      setCustomResult(data.combined ?? data.results?.[0] ?? null);
    } catch (err) {
      console.error("Custom sizing failed:", err);
      const parsed = err.parsedError || parseApiError(err);
      setAnalyzeError({ ...parsed, retryable: true });
      setCustomResult(null);
    } finally {
      setCustomLoading(false);
    }
  }, [username, selectedNames, branchFilter, sizingCase]);

  useEffect(() => {
    setSelectedNames(new Set());
    setCustomResult(null);
  }, [branchFilter, viewMode]);

  useEffect(() => {
    if (viewMode !== MODE_CUSTOM || selectedNames.size === 0) return;
    loadCustomSelection();
  }, [sizingCase]);

  const toggleMachine = (name) => {
    setSelectedNames((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
    setCustomResult(null);
  };

  const selectAllMachines = () => {
    setSelectedNames(new Set(machinesInScope.map((m) => m.name)));
    setCustomResult(null);
  };

  const clearSelection = () => {
    setSelectedNames(new Set());
    setCustomResult(null);
  };

  const scopeLabel = branchFilter === ALL_BRANCHES ? "All branches" : branchFilter;
  const mainSlotLabel = `Main ${mainSlotFilter}`;

  const dateRangeLabel = useMemo(() => {
    if (!dateRange?.start || !dateRange?.end) return "past year";
    return `${formatReportDateShort(dateRange.start)} – ${formatReportDateShort(dateRange.end)}`;
  }, [dateRange]);

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <h1 style={styles.title}>Off-Grid Sizing</h1>
        <p style={styles.subtitle}>
          Based on the past year of meter data ({dateRangeLabel}), excluding solar & generator.
        </p>
      </header>

      <div style={styles.toolbar}>
        <div style={styles.modeToggle}>
          <button
            type="button"
            style={{
              ...styles.modeBtn,
              ...(viewMode === MODE_ALL_MAINS ? styles.modeBtnActive : {}),
            }}
            onClick={() => setViewMode(MODE_ALL_MAINS)}
          >
            All mains
          </button>
          <button
            type="button"
            style={{
              ...styles.modeBtn,
              ...(viewMode === MODE_CUSTOM ? styles.modeBtnActive : {}),
            }}
            onClick={() => setViewMode(MODE_CUSTOM)}
          >
            Custom
          </button>
        </div>

        <label style={styles.toolbarField}>
          <span>Branch</span>
          <select
            style={styles.select}
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
            disabled={scopeLoading}
          >
            <option value={ALL_BRANCHES}>All branches</option>
            {branchOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>

        {viewMode === MODE_ALL_MAINS && hasAnyMains && (
          <div style={styles.mainSlotToggle}>
            {MAIN_SLOTS.map((slot) => (
              <button
                key={slot}
                type="button"
                style={{
                  ...styles.modeBtn,
                  ...(mainSlotFilter === String(slot) ? styles.modeBtnActive : {}),
                }}
                onClick={() => setMainSlotFilter(String(slot))}
              >
                Main {slot}
              </button>
            ))}
          </div>
        )}

        <div style={styles.mainSlotToggle}>
          <button
            type="button"
            style={{
              ...styles.modeBtn,
              ...(sizingCase === SIZING_WORST ? styles.modeBtnActive : {}),
            }}
            onClick={() => setSizingCase(SIZING_WORST)}
          >
            Worst case
          </button>
          <button
            type="button"
            style={{
              ...styles.modeBtn,
              ...(sizingCase === SIZING_BEST ? styles.modeBtnActive : {}),
            }}
            onClick={() => setSizingCase(SIZING_BEST)}
          >
            Best case
          </button>
        </div>
      </div>

      {scopeLoading && <p style={styles.muted}>Loading facility hierarchy…</p>}
      {scopeError && (
        <ApiErrorAlert
          error={scopeError}
          title="Could not load facility scope"
          onRetry={loadScope}
        />
      )}
      {analyzeError && !scopeError && (
        <ApiErrorAlert
          error={analyzeError}
          title="Sizing analysis failed"
          onRetry={viewMode === MODE_ALL_MAINS ? loadAllMains : loadCustomSelection}
        />
      )}

      {!scopeLoading && !scopeError && (
        <div style={styles.layout}>
          {viewMode === MODE_CUSTOM && (
            <section style={styles.machinePanel}>
              <div style={styles.panelHead}>
                <h2 style={styles.panelTitle}>Select machines</h2>
                <span style={styles.countTag}>{selectedNames.size} selected</span>
              </div>

              <div style={styles.selectionActions}>
                <button type="button" style={styles.linkBtn} onClick={selectAllMachines}>
                  Select all
                </button>
                <button type="button" style={styles.linkBtn} onClick={clearSelection}>
                  Clear
                </button>
              </div>

              {machinesInScope.length === 0 ? (
                <p style={styles.muted}>No machines in this branch.</p>
              ) : (
                <div style={styles.machineList}>
                  {machinesInScope.map((machine, index) => {
                    const isSelected = selectedNames.has(machine.name);
                    const badge = categoryLabel(machine.category, machine.isMain);
                    return (
                      <button
                        key={`${machine.name}-${index}`}
                        type="button"
                        onClick={() => toggleMachine(machine.name)}
                        style={{
                          ...styles.machineItem,
                          ...(isSelected ? styles.machineItemActive : {}),
                        }}
                      >
                        <span style={styles.checkBox}>{isSelected ? "✓" : ""}</span>
                        <span style={styles.machineName}>{machine.name}</span>
                        <span style={styles.badgeSmall}>{badge}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              <button
                type="button"
                style={styles.analyzeBtn}
                disabled={customLoading || selectedNames.size === 0}
                onClick={loadCustomSelection}
              >
                {customLoading ? "Analyzing…" : `Analyze ${selectedNames.size || ""} selected`}
              </button>
            </section>
          )}

          <section style={styles.resultsPanel}>
            {viewMode === MODE_ALL_MAINS && (
              <>
                <div style={styles.resultsHead}>
                  <h2 style={styles.panelTitle}>All mains</h2>
                  <span style={styles.scopeTag}>
                    {scopeLabel} · {mainSlotLabel} · {machinesInScope.length} machine
                    {machinesInScope.length !== 1 ? "s" : ""}
                  </span>
                </div>

                {bulkLoading && <p style={styles.muted}>Analyzing mains…</p>}

                {!bulkLoading && mainsToSize.length === 0 && (
                  <p style={styles.muted}>
                    No Main {mainSlotFilter} meter found for this branch.
                  </p>
                )}

                {!bulkLoading &&
                  mainCombinedResult &&
                  hasLoadData(mainCombinedResult.loadAnalysis) && (
                    <div style={styles.mainsList}>
                      <SizingBlock result={mainCombinedResult} sizingCase={sizingCase} />
                    </div>
                  )}

                {!bulkLoading &&
                  mainCombinedResult &&
                  !hasLoadData(mainCombinedResult.loadAnalysis) && (
                    <p style={styles.muted}>No energy data found for the past year.</p>
                  )}
              </>
            )}

            {viewMode === MODE_CUSTOM && (
              <>
                <div style={styles.resultsHead}>
                  <h2 style={styles.panelTitle}>Custom sizing</h2>
                  {selectedNames.size > 0 && (
                    <span style={styles.scopeTag}>{selectedNames.size} machine(s)</span>
                  )}
                </div>

                {selectedNames.size === 0 && (
                  <p style={styles.muted}>Select one or more machines, then click Analyze.</p>
                )}

                {selectedNames.size > 0 && !customResult && !customLoading && (
                  <p style={styles.muted}>Click Analyze to load sizing for your selection.</p>
                )}

                {customLoading && <p style={styles.muted}>Analyzing selected machines…</p>}

                {!customLoading && customResult && (
                  <div style={styles.mainsList}>
                    <SizingBlock result={customResult} sizingCase={sizingCase} />
                  </div>
                )}

                {!customLoading && selectedNames.size > 0 && customResult === null && analyzeError && (
                  <p style={styles.muted}>No energy data found for the past year.</p>
                )}
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

const styles = {
  page: {
    padding: "28px 24px 40px",
    maxWidth: 1100,
    margin: "0 auto",
  },
  header: {
    marginBottom: 20,
  },
  title: {
    margin: "0 0 6px",
    fontSize: 24,
    fontWeight: 700,
    color: "#0f172a",
    letterSpacing: "-0.02em",
  },
  subtitle: {
    margin: 0,
    color: "#64748b",
    fontSize: 14,
    lineHeight: 1.5,
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    alignItems: "flex-end",
    marginBottom: 20,
    padding: "14px 16px",
    background: "#fff",
    border: "1px solid #e8edf2",
    borderRadius: 12,
  },
  modeToggle: {
    display: "flex",
    gap: 4,
    padding: 3,
    background: "#f1f5f9",
    borderRadius: 9,
  },
  modeBtn: {
    padding: "7px 14px",
    border: "none",
    borderRadius: 7,
    background: "transparent",
    fontSize: 13,
    fontWeight: 600,
    color: "#64748b",
    cursor: "pointer",
  },
  modeBtnActive: {
    background: "#fff",
    color: "#0f766e",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  mainSlotToggle: {
    display: "flex",
    gap: 4,
    padding: 3,
    background: "#e8f4f2",
    borderRadius: 9,
  },
  toolbarField: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
    fontSize: 11,
    fontWeight: 700,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    minWidth: 160,
  },
  select: {
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #e2e8f0",
    fontSize: 14,
    color: "#334155",
    background: "#fff",
    textTransform: "none",
    fontWeight: 400,
    letterSpacing: 0,
  },
  layout: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 16,
    alignItems: "start",
  },
  machinePanel: {
    background: "#fff",
    border: "1px solid #e8edf2",
    borderRadius: 12,
    padding: "16px 18px",
    maxHeight: "70vh",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  panelHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  panelTitle: {
    margin: 0,
    fontSize: 13,
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  countTag: {
    fontSize: 12,
    fontWeight: 600,
    color: "#0f766e",
  },
  selectionActions: {
    display: "flex",
    gap: 12,
  },
  linkBtn: {
    padding: 0,
    border: "none",
    background: "none",
    color: "#0f766e",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  machineList: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    flex: 1,
    overflowY: "auto",
  },
  machineItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    padding: "10px 12px",
    border: "1px solid transparent",
    borderRadius: 8,
    background: "transparent",
    cursor: "pointer",
    textAlign: "left",
    fontSize: 14,
    color: "#1e293b",
  },
  machineItemActive: {
    background: "#f0fdf9",
    borderColor: "#b8e8df",
    color: "#0f766e",
    fontWeight: 600,
  },
  checkBox: {
    width: 16,
    flexShrink: 0,
    fontSize: 12,
    color: "#1BA08E",
  },
  machineName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
  },
  badgeSmall: {
    flexShrink: 0,
    fontSize: 9,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "#64748b",
    background: "#f1f5f9",
    padding: "2px 6px",
    borderRadius: 999,
  },
  analyzeBtn: {
    marginTop: 4,
    padding: "10px 16px",
    border: "none",
    borderRadius: 8,
    background: "#1BA08E",
    color: "#fff",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  resultsPanel: {
    background: "#fafcfb",
    border: "1px solid #d9ece8",
    borderRadius: 12,
    padding: "18px 20px",
  },
  resultsHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 16,
  },
  scopeTag: {
    fontSize: 13,
    fontWeight: 600,
    color: "#0f766e",
  },
  mainsList: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  mainBlock: {
    background: "#fff",
    border: "1px solid #e2ede9",
    borderRadius: 10,
    padding: "16px 18px",
  },
  mainBlockHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 12,
    fontSize: 15,
    fontWeight: 700,
    color: "#0f172a",
  },
  badge: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "#1e40af",
    background: "#dbeafe",
    padding: "2px 8px",
    borderRadius: 999,
  },
  machineListNote: {
    margin: "0 0 12px",
    fontSize: 13,
    color: "#64748b",
    lineHeight: 1.5,
  },
  resultsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 10,
  },
  resultTile: {
    background: "#f8fafc",
    border: "1px solid #eef2f6",
    borderRadius: 8,
    padding: "12px 14px",
  },
  resultLabel: {
    display: "block",
    fontSize: 10,
    fontWeight: 700,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: 4,
  },
  resultValue: {
    display: "block",
    fontSize: 18,
    fontWeight: 700,
    color: "#0f766e",
    letterSpacing: "-0.02em",
  },
  resultDetail: {
    display: "block",
    marginTop: 4,
    fontSize: 11,
    color: "#64748b",
    lineHeight: 1.4,
  },
  analyzerSection: {
    marginBottom: 16,
  },
  analyzerTitle: {
    margin: "0 0 10px",
    fontSize: 12,
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  solarSection: {
    borderTop: "1px solid #e2ede9",
    paddingTop: 16,
  },
  solarSectionTitle: {
    margin: "0 0 14px",
    fontSize: 12,
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  muted: {
    margin: 0,
    color: "#94a3b8",
    fontSize: 14,
  },
  error: {
    margin: 0,
    color: "#dc2626",
    fontSize: 14,
  },
};

export default OffGridCalculatorTab;
